/*----------------------------------
- TLD PAGES CONTENT CATALOG (SSOT)
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @adr  ADR-0055
 * @rule Public TLD copy states registry facts, aggregates and coarse bands only: no score, no
 *       ranking, no individual sale, no count behind a band, and never where the sales data
 *       comes from. One section per page: `hub` (`/tld`), `insight` (`/tld/<slug>`) and
 *       `applications` (`/tld/applications`).
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { TLD_INSIGHT_SAMPLE_LIMIT } from '@uniquedomains/core/domains/tlds';

import { TLD_APPLICATIONS_BOARD_DEFAULT_COPY, type TldApplicationsBoardCopy } from '@uniquedomains/components/tlds/applications/boardCopy';
import { TLD_BOARD_DEFAULT_COPY, type TldBoardCopy } from '@uniquedomains/components/tlds/board/boardCopy';
import { TLD_PAGE_DEFAULT_COPY, type TldPageCopy } from '@uniquedomains/components/tlds/page/pageCopy';

/*----------------------------------
- APPLICATIONS (2026 ROUND)
----------------------------------*/

/**
 * The website's words of the shared applications board (ADR-0065). The pre-reveal body is the
 * board's own, worded from the round's Reveal Day (`TLD_ROUND_2026_REVEAL_DAY`, also the agenda's
 * `AGENDA_REVEAL_DAY`): a slipped date is a one-line change there.
 */
// The page head's title and lede ride beside the board's words (the board draws no title of its own).
const TLD_APPLICATIONS_BOARD_COPY: TldApplicationsBoardCopy & { title: string; lede: string } = {
    ...TLD_APPLICATIONS_BOARD_DEFAULT_COPY,
    title: 'New gTLD applications',
    lede: 'Every string applied for in ICANN’s 2026 round, as ICANN publishes it: applicant, type, status and contention set. Two bands say whether the string is a word people use and whether that word already sells as a domain.',
    preRevealNote: null,
    preRevealLink: 'See the launch dates on the agenda',
};

/*----------------------------------
- PAGES
----------------------------------*/

/*
 * Kind, root zone status, IANA type, quality band, sales trend and keyword facet words are the
 * shared TLD labels (`@uniquedomains/components/contracts/tlds/labels`), and every card of the
 * insight page is worded by the shared page copy: one wording for Product and the website.
 */
const TLD_INSIGHT_PAGE_COPY: TldPageCopy = { ...TLD_PAGE_DEFAULT_COPY, backLink: 'Every TLD' };

const TLD_HUB_BOARD_COPY: TldBoardCopy = {
    ...TLD_BOARD_DEFAULT_COPY,
    title: 'Every TLD',
    lede: 'Every top-level domain of the root zone: status, launch dates and registry changes for all of them, market figures and a page for the public set.',
};

export const TLD_PAGE_CONTENT = {
    /**
     * The persona pair's card, on the hub and on every insight page. The two intent verbs (ADR-0091): Radar when the
     * result is names (a TLD's open and resale names, a launching TLD included), Watch when it is a change (a live
     * TLD's prices). Before the second self-review of batch 4 route 2 part 2 (finding 15) the card watched a TLD and
     * watched launches "with a Radar", mixing the two.
     */
    personas: {
        heading: 'Set a Radar on a TLD, or find a name under it',
        body: 'Investors set a Radar on a TLD for its open and resale names, a launching one included, and watch a live TLD’s prices. Founders start from a search and get one-word names under the TLDs that fit.',
    },
    hub: {
        meta: {
            pageTitle: 'TLD pages: registry status, prices, sales and launch dates',
            metaDescription:
                'Registry status, registrar prices, 12-month sales figures and market size for the public TLDs, with every new TLD launch and root zone change. Every scanned TLD is in the product.',
            markdownTitle: 'Markdown version',
        },
        /** The shared TLD board's words (ADR-0063), with the website's title and lede. */
        board: TLD_HUB_BOARD_COPY,
        /** The title pill: "See every figure", never "unlock" (the website copy rule). */
        unlock: 'See every figure',
        relatedLinksTitle: 'Continue with Unique Domains',
    },
    insight: {
        meta: {
            pageTitle: (tld: string) => `${tld} TLD: registry, prices, sales and launch dates`,
            metaDescription: (tld: string) =>
                `${tld} registry status, registrar prices, sales month by month, market size and ${TLD_INSIGHT_SAMPLE_LIMIT} sample ${tld} names, counted every night.`,
            conciseMarkdownTitle: 'AI context markdown',
            fullMarkdownTitle: 'Full Markdown version',
        },
        /** The shared page's words (ADR-0062), with the website's way back to the hub. */
        page: TLD_INSIGHT_PAGE_COPY,
        /** The mirror's opening paragraph: the page says it with its hero and figures. */
        lede: (tld: string) =>
            `What ${tld} is, what it costs, what names under it sell for and how many one-word names are still available. Counted every night.`,
        hero: {
            // The wall's label follows the two verbs (ADR-0091, batch 7 report decision 16): Radar names its object, never "Follow".
            radarOn: (tld: string) => `Radar on ${tld}`,
            getDataset: 'Get the dataset',
        },
        sample: {
            heading: `${TLD_INSIGHT_SAMPLE_LIMIT} one-word names`,
            caption: (tld: string) => `Sample of one-word ${tld} names`,
            domain: 'Name',
            status: 'Status',
            price: 'Price',
            noPrice: 'No price',
            empty: 'No name to show yet.',
        },
        siblings: {
            heading: 'Other TLD pages',
            everyTld: 'See every TLD',
            markdown: 'Read this page as Markdown',
            api: 'Read the API endpoint',
        },
        /** The page's agenda link, in "Other TLD pages", the related links and the mirror. */
        agendaLink: (tld: string) => `See ${tld} dates on the agenda`,
        mirror: {
            /** The monthly sales table's heads. */
            month: 'Month',
            sales: 'Sales',
            relatedLinks: 'Related links',
            github: 'The dataset on GitHub',
        },
        related: {
            title: 'Continue with Unique Domains',
            hub: 'See every public TLD page',
            radars: 'Save a repeatable search with Radars',
        },
        unavailable: 'This TLD page is being prepared. Please try again shortly.',
    },
    applications: {
        meta: {
            pageTitle: '2026 new gTLD applications: every applied-for string',
            pageTitleWithPage: (page: number) => `2026 new gTLD applications, page ${page}`,
            metaDescription:
                'Every application in ICANN’s 2026 new gTLD round: string, applicant, type, status and contention set, with coarse word and comparable-sales bands.',
            markdownTitle: 'Markdown version',
        },
        board: TLD_APPLICATIONS_BOARD_COPY,
        /**
         * The head's call to action: Full Access watches strings in Product (no free plan since ADR-0083). A string is
         * watched, never followed (the two intent verbs, ADR-0091); the wall it opens keeps its trigger and action.
         */
        watchPill: 'Watch a string',
        /** The rail's dark card, under the round's timeline. */
        rail: {
            heading: 'Watch a string from application to launch',
            body: 'Hear when its status, contention set or delegation changes. A launch Radar starts the day it delegates and lists the one-word names under it.',
            cta: 'Get Full Access',
        },
        /**
         * What exists for an applied-for string, in the present tense: the Wednesday agenda email (the launch dates ahead
         * and the registry changes, every new delegation among them), and a launch Radar a member arms on the string
         * before it delegates (Product's applications page, as the rail card says): it starts the day the string
         * delegates, and a legacy `count` plan sees a count, not the names. Reveal Day (7 Oct 2026) is past, so no line
         * announces it (second self-review of batch 4 route 2 part 2, finding 7).
         */
        watch: {
            heading: 'Watch a new TLD from application to launch',
            body: 'The Wednesday agenda email lists the launch dates ahead and every new delegation. Arm a launch Radar on a string today: it starts the day the string delegates, and Full Access shows the matching names.',
            cta: 'Get Full Access',
        },
        relatedLinksTitle: 'Continue with Unique Domains',
        unavailable: 'The applications list is temporarily unavailable. Please try again shortly.',
    },
} as const;
